import { Injectable, inject } from '@angular/core'
import { HttpService } from '@bases/http.service'
import { API_URL, CM_LAYERS_ORDER } from '@core/constants/constant.data'
import { GEOJSON_FORMAT, RASTER_FORMAT, VECTOR_FORMAT } from '@core/constants/layers.data'
import { FeatureCollectionWithFileName } from '@core/models/calculation-module.model'
import { GeojsonService } from '@core/services/geojson.service'
import { SLDService } from '@core/services/sld.service'
import { ToasterService } from '@core/services/toaster.service'
import { isNullOrUndefinedString } from '@services/core.utilities'
import Keycloak from 'keycloak-js'
import * as L from 'leaflet'
import shp from 'shpjs'
import { SessionManagerService } from './session-manager.service'

@Injectable({
  providedIn: 'root',
})
export class CMLayersService extends HttpService {
  readonly keycloak = inject(Keycloak)
  protected _baseUrl = API_URL + '/cm/'
  readonly sessionManagerService = inject(SessionManagerService)

  toasterService = inject(ToasterService)
  private readonly _geoJsonService = inject(GeojsonService)
  private readonly _sldService = inject(SLDService)

  isLoggedIn$ = this.sessionManagerService.isLoggedIn$

  layersCM = new L.FeatureGroup()

  private _cmLayersArray = new Map<string, L.Layer>()
  private _layerErrorMessage =
    'Something went wrong. The layer could not be retrieved from the CM and is not showing on the map.'

  constructor() {
    super()
  }

  /**
   * Build URL with token only if user is logged in AND cm_session is true
   */
  //@todo, check if useful to have token update logic here
  private async _buildUrlWithToken(
    baseUrl: string,
    is_session: boolean,
    directoryUUID?: string,
  ): Promise<string> {
    // Only add token if both conditions are met
    if (!is_session) {
      return baseUrl
    }

    if (!this.isLoggedIn$()) {
      return baseUrl
    }

    try {
      if (!this.keycloak?.token) {
        return baseUrl
      }

      // Refresh token if needed
      if (this.keycloak.isTokenExpired()) {
        await this.keycloak.updateToken(30)
      }

      const separator = baseUrl.includes('?') ? '&' : '?'
      if (directoryUUID) {
        return `${baseUrl}${separator}token=${encodeURIComponent(this.keycloak.token)}${separator}cmSessionId=${directoryUUID}`
      }
      return `${baseUrl}${separator}token=${encodeURIComponent(this.keycloak.token)}`
    } catch (error) {
      console.warn('Token refresh failed:', error)
      return baseUrl
    }
  }

  removeLayerFromMap(layerUUID: string, type: string) {
    const layer = this._cmLayersArray.get(layerUUID)

    if (layer == undefined) {
      console.warn('The layer cannot be remove of the map, not found', layerUUID)
      return
    }

    if (type === VECTOR_FORMAT) {
      ;(layer as L.GeoJSON).clearLayers()
    } else if (type === RASTER_FORMAT) {
      this.layersCM.removeLayer(layer)
    } else if (type === GEOJSON_FORMAT) {
      ;(layer as L.GeoJSON).clearLayers()
    }
    this._cmLayersArray.delete(layerUUID)
  }

  async _addLayerWithActionRaster(
    layerUUID: string,
    isSession: boolean = false,
    directoryUUID?: string,
  ) {
    try {
      const maxNativeZoom = await this._getMaxZoom(layerUUID, isSession, directoryUUID)

      const tileLayerUrl = await this._buildUrlWithToken(
        `${API_URL}/cm/tiles/${layerUUID}/{z}/{x}/{y}?cmSession=${isSession}`,
        isSession,
        directoryUUID,
      )

      const layer = L.tileLayer(tileLayerUrl, {
        zIndex: CM_LAYERS_ORDER,
        maxNativeZoom,
        tms: true,
      })

      this._cmLayersArray.set(layerUUID, layer)
      return layer.addTo(this.layersCM)
    } catch (error) {
      console.error('Error in _addLayerWithActionRaster:', error)
      this.toasterService.openSnackBarError(this._layerErrorMessage)
    }
  }

  private async _getMaxZoom(
    layerUUID: string,
    isSession: boolean = false,
    directoryUUID?: string,
  ): Promise<number> {
    const url = await this._buildUrlWithToken(
      this._baseUrl + `maxzoom/${layerUUID}?cmSession=${isSession}`,
      isSession,
      directoryUUID,
    )

    const response = await this.GET(url)
    return response?.maxZoom || 14 // Fallback
  }

  private async _addLayerWithActionVector(
    layerUUID: string,
    is_session: boolean = false,
    directoryUUID?: string,
  ) {
    try {
      const shpUrl = await this._buildUrlWithToken(
        `${API_URL}/cm/files/${layerUUID}?cmSessionId=${directoryUUID}&cmSession=${is_session}`,
        is_session,
      )

      shp(shpUrl)
        .then((data) => {
          const layer = new L.GeoJSON(data as FeatureCollectionWithFileName, {
            onEachFeature: this.onEachFeature,
            style: (feature) => {
              let color = '#FFA500'
              let fillColor = '#FFA500'
              const fillOpacity = 0.5

              if (!isNullOrUndefinedString(feature?.properties['color']))
                color = feature?.properties['color']
              if (!isNullOrUndefinedString(feature?.properties['fillColor']))
                fillColor = feature?.properties['fillColor']
              if (!isNullOrUndefinedString(feature?.properties['opacity']))
                color = feature?.properties['opacity'] // Note: This looks like it should be fillOpacity?

              return {
                color: color,
                fillColor: fillColor,
                fillOpacity: fillOpacity,
              }
            },
          })

          layer.addTo(this.layersCM)
          this._cmLayersArray.set(layerUUID, layer)
        })
        .catch((error) => {
          console.warn('_addLayerWithActionVector', error)
          this.toasterService.openSnackBarError(this._layerErrorMessage)
        })
    } catch (error) {
      console.error('Error in _addLayerWithActionVector:', error)
      this.toasterService.openSnackBarError(this._layerErrorMessage)
    }
  }

  private async _addLayerWithActionGeoJson(
    layerUUID: string,
    symbology: string,
    isSession: boolean = false,
    directoryUUID?: string,
  ) {
    try {
      const url = await this._buildUrlWithToken(
        this._baseUrl +
          `layer/geojson?path=${layerUUID}&cmSessionId=${directoryUUID}&cmSession=${isSession}`,
        isSession,
      )
      const response = await this.GET(url)
      if (response === null) return

      let layer
      const xmlSld = await this._sldService.getStyleFromGeoserver(symbology)

      if (xmlSld === null) {
        layer = await this._geoJsonService.buildGeoJSONLayerGroup(response)
      } else {
        layer = await this._geoJsonService.buildStylizedGeoJsonLayerGroup(response, xmlSld)
      }

      layer.addTo(this.layersCM)
      this._cmLayersArray.set(layerUUID, layer)
    } catch (error) {
      console.error('Error in _addLayerWithActionGeoJson:', error)
      this.toasterService.openSnackBarError(this._layerErrorMessage)
    }
  }

  addLayerCM(
    layerUUID: string,
    type: string,
    symbology = '',
    is_session: boolean = false,
    directoryUUID?: string,
  ) {
    if (this._cmLayersArray.has(layerUUID)) {
      console.warn('This layer is already active', layerUUID)
      return
    }

    if (type === RASTER_FORMAT) {
      if (directoryUUID) {
        return this._addLayerWithActionRaster(layerUUID, is_session, directoryUUID)
      }
      return this._addLayerWithActionRaster(layerUUID, is_session)
    } else if (type === VECTOR_FORMAT) {
      if (directoryUUID) {
        return this._addLayerWithActionVector(layerUUID, is_session, directoryUUID)
      }
      return this._addLayerWithActionVector(layerUUID, is_session)
    } else if (type === GEOJSON_FORMAT) {
      if (directoryUUID) {
        return this._addLayerWithActionGeoJson(layerUUID, symbology, is_session, directoryUUID)
      }
      return this._addLayerWithActionGeoJson(layerUUID, symbology, is_session)
    }
  }

  onEachFeature(feature: any, layer: L.Layer) {
    let html = ''
    Object.keys(feature.properties).map((prop) => {
      if (prop != 'color' && prop != 'fillColor' && prop != 'opacity') {
        html += '<bold>' + prop + ':</bold>'
        html += '<span>' + feature.properties[prop] + '</span><br />'
      }
    })
    layer.bindPopup(html)
  }

  clearAll() {
    this.layersCM.clearLayers()
  }
}
